import { CarrierVerificationErrorCode, McVerificationDocType, McVerificationDocView, McVerificationStatusView } from '@numeo/types-core'
import axios, { isAxiosError } from 'axios'

// Shared DTOs live in @numeo/types so the producer (main-service) and this consumer never drift.
export type { McVerificationDocView, McVerificationStatusView } from '@numeo/types-core'

/** A failed MC-verification request: the server's message for the customer, and its `errorCode` for analytics. */
export class McVerificationRequestError extends Error {
    constructor(
        message: string,
        readonly errorCode?: CarrierVerificationErrorCode
    ) {
        super(message)
        this.name = 'McVerificationRequestError'
    }
}

function requestError(error: unknown, fallback: string): McVerificationRequestError {
    if (!isAxiosError(error)) return new McVerificationRequestError(fallback)
    const data = error.response?.data as { message?: string; errorCode?: CarrierVerificationErrorCode } | undefined
    return new McVerificationRequestError(data?.message || fallback, data?.errorCode)
}

export const mcVerificationApi = {
    async getStatus(): Promise<McVerificationStatusView> {
        const response = await axios.get<{ success: boolean; data: McVerificationStatusView }>('/application/mc-verification/status')
        return response.data.data
    },

    async sendEmailCode(): Promise<{ sentToMasked: string; expiresAt: string }> {
        try {
            const response = await axios.post<{ success: boolean; data: { sentToMasked: string; expiresAt: string } }>(
                '/application/mc-verification/email/send-code'
            )
            return response.data.data
        } catch (error) {
            throw requestError(error, 'Failed to send verification code')
        }
    },

    async verifyEmailCode(code: string): Promise<McVerificationStatusView> {
        try {
            const response = await axios.post<{ success: boolean; data: McVerificationStatusView }>('/application/mc-verification/email/verify', { code })
            return response.data.data
        } catch (error) {
            throw requestError(error, 'Failed to verify code')
        }
    },

    async uploadDocuments(files: Partial<Record<McVerificationDocType, File>>, usesFactoring: boolean): Promise<McVerificationStatusView> {
        const formData = new FormData()
        for (const [type, file] of Object.entries(files)) {
            if (file) formData.append(type, file)
        }
        formData.append('usesFactoring', String(usesFactoring))
        try {
            const response = await axios.post<{ success: boolean; data: McVerificationStatusView }>('/application/mc-verification/documents', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            return response.data.data
        } catch (error) {
            throw requestError(error, 'Failed to upload documents')
        }
    },

    async getDocuments(): Promise<McVerificationDocView[]> {
        const response = await axios.get<{ success: boolean; data: McVerificationDocView[] }>('/application/mc-verification/documents')
        return response.data.data
    },

    // Switch the account onto the multi-MC (parent-company) model before verifying. Handles both
    // single-MC (flag-flip only) and multi-MC (split) accounts; the backend is idempotent.
    async migrate(): Promise<{ migrated: boolean; alreadyMigrated: boolean; splitsCreated: number; applicationIds: string[] }> {
        try {
            const response = await axios.post<{
                success: boolean
                data: { migrated: boolean; alreadyMigrated: boolean; splitsCreated: number; applicationIds: string[] }
            }>('/application/mc-verification/migrate')
            return response.data.data
        } catch (error) {
            throw requestError(error, 'Failed to switch to the multi-MC experience')
        }
    },
}
